/* eslint-disable no-console */

import React, { useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, ThemeProvider, View } from '@fairygodboss/ui';
import { PageContext } from '../../../contexts/ContextCreator';
import BasicDropdown from '../../Input/BasicDropdown/BasicDropdown';
import EmbedlyPreview from '../EmbedlyPreview/EmbedlyPreview';
import useMount from '../../../utilities/hooks/useMount';
import CheckBox from '../../Content/Elements/CheckBox/CheckBox';
import { constructCn } from '../../../utilities/helperFunctions';
// @TODO add hashtagImgSrc import when adding hashtag functionality.
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';

import './ButtonPanel.scss';
import CloudinaryImageUpload from '../../CloudinaryImageUpload/CloudinaryImageUpload';
import PhotoPreview from '../PhotoPreview/PhotoPreview';
import Camera from './Camera';

const cloudinaryWidgetOptions = {
    defaultCoundinaryID: 'icons/user-default',
    uploadPreset: 'community-photo',
    showSkipCropButton: true,
    clientAllowedFormats: ['jpeg', 'png', 'heif', 'webp', 'gif'],
};

const ButtonPanel = ({
    dispatch,
    // insertHashtag,
    closeHandler,
    isAnonymousPost,
    communityGroupHash,
    disablePhoto,
    pageGroup,
    embedlyUrl,
    commentDepth,
    previewDisabled = false,
    editMode = false,
    handleCancelClick,
    handleUpdateClick,
    handleTogglePostAnon,
    hash,
    saveButtonDisabled,
    image,
}) => {
    const { userFullGroupList = [] } = useContext(PageContext);

    useMount(() => {
        if (pageGroup.hash) {
            dispatch('SELECT_POST_GROUP', { value: pageGroup.hash });
        }
    });

    const canPostToMultipleGroups = useMemo(() => {
        return !!(userFullGroupList.length > 0);
    }, [userFullGroupList.length]);

    const groupOptions = useMemo(() => {
        return [
            { value: '', label: 'Fairygodboss Feed' },
            ...(userFullGroupList || []).map((group) => ({
                value: group.hash,
                label: group.name,
            })),
        ];
    }, [userFullGroupList]);

    const handleToggleAnonymity = useCallback(() => handleTogglePostAnon(), [
        dispatch,
    ]);

    const handleGroupPostingSelection = useCallback(
        ({ target: { value } }) => dispatch('SELECT_POST_GROUP', { value }),
        [dispatch]
    );

    const anonCheckboxClassname = constructCn(
        'post-options-row__section',
        'anon-post-checkbox',
        isAnonymousPost && 'post-anon-bold',
        editMode && 'edit-mode-on',
        commentDepth >= 2 && 'deep-comment'
    );

    const savedChangedButtonClass = constructCn(
        'save-changes-button',
        saveButtonDisabled && 'disabled',
        commentDepth >= 2 && 'deep-comment'
    );
    const buttonContainerClass = constructCn(
        'post-options-row__section',
        'edit-mode-buttons',
        commentDepth >= 2 && 'deep-comment'
    );
    const cancelButtonClass = constructCn(
        'cancel-button',
        commentDepth >= 2 && 'deep-comment'
    );
    let UnifiedPostBoxId = 'unified-post-anon';
    if (hash) UnifiedPostBoxId += `-${hash}`;

    const handleCloudinaryUpdate = useCallback(
        (cloudinaryId) => {
            dispatch('SET_IMAGE_URL', { value: cloudinaryId });
            dispatch('CLEAR_PHOTO_UPLOAD_ERROR');
            EmitMetric({
                misc_event_type: 'photo-preview-success',
                misc_event_count: 1,
                cloudinary_id: cloudinaryId,
            });
        },
        [dispatch]
    );

    const handleCloudinaryFailure = useCallback(
        (error, result) => {
            if (error && result?.type?.includes('video')) {
                dispatch('SET_PHOTO_UPLOAD_ERROR', {
                    data: {
                        errors: [
                            'Sorry, videos are not supported at this time.',
                        ],
                    },
                });
            }
            const timestamp = new Date();
            EmitMetric({
                misc_event_type: 'photo-preview-failure',
                misc_event_count: 1,
                timestamp,
            });
        },
        [dispatch]
    );

    const handleClickMetric = useCallback(() => {
        EmitMetric({
            misc_event_type: 'photo-upload-button-click',
            misc_event_count: 1,
        });
    }, [dispatch]);

    return (
        <div className="component-UnifiedPostBox-ButtonPanel">
            {!editMode && (
                <div className="editor-action-buttons">
                    {/* TODO: enable after launch */}
                    {/* <button
                        className="hashtag-button"
                        type="button"
                        tabIndex={0}
                        onClick={insertHashtag}
                    >
                        <img src={hashtagImgSrc} alt="insert-hashtag" />
                    </button> */}
                    <CloudinaryImageUpload
                        {...cloudinaryWidgetOptions}
                        successCallback={handleCloudinaryUpdate}
                        onUploadFailure={handleCloudinaryFailure}
                        uploadSources={['local']}
                        disabled={disablePhoto}
                    >
                        <button
                            className="image-button"
                            type="button"
                            tabIndex={0}
                            disabled={disablePhoto}
                            onClick={handleClickMetric}
                        >
                            <Camera />
                        </button>
                    </CloudinaryImageUpload>
                </div>
            )}
            {embedlyUrl && !previewDisabled && (
                <EmbedlyPreview
                    url={embedlyUrl}
                    send={dispatch}
                    closeHandler={closeHandler}
                    editMode={editMode}
                />
            )}
            {image?.length > 0 && (
                <PhotoPreview
                    image={{ src: image, alt: 'preview-image' }}
                    send={dispatch}
                    editMode={editMode}
                />
            )}
            <div className="post-options-row">
                <div className={anonCheckboxClassname}>
                    <CheckBox
                        id={UnifiedPostBoxId}
                        defaultChecked={isAnonymousPost}
                        inputValue={isAnonymousPost}
                        handleChange={handleToggleAnonymity}
                    />
                    Post anonymously
                </div>
                {editMode && (
                    <ThemeProvider>
                        <View className={buttonContainerClass}>
                            <Button
                                variant="secondary"
                                size="large"
                                mr="16px"
                                onPress={() => {
                                    handleCancelClick();
                                }}
                                className={cancelButtonClass}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                size="large"
                                mr="16px"
                                onPress={() => {
                                    handleUpdateClick();
                                }}
                                className={savedChangedButtonClass}
                                disabled={saveButtonDisabled}
                            >
                                Save Changes
                            </Button>
                        </View>
                    </ThemeProvider>
                )}
                {canPostToMultipleGroups && !editMode && (
                    <div className="post-options-row__section group-selector">
                        <BasicDropdown
                            cssClass="select--post_to_groups"
                            controlLabel={
                                // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                <label htmlFor="post-group-selector">
                                    Post to:
                                </label>
                            }
                            handleRawChangeEvent={handleGroupPostingSelection}
                            selectedValue={communityGroupHash}
                            options={groupOptions}
                            idValue="post-group-selector"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

ButtonPanel.propTypes = {
    dispatch: PropTypes.func.isRequired,
    closeHandler: PropTypes.func.isRequired,
    // insertHashtag: PropTypes.func.isRequired,
    isAnonymousPost: PropTypes.bool.isRequired,
    communityGroupHash: PropTypes.string,
    disablePhoto: PropTypes.bool,
    pageGroup: PropTypes.shape({
        hash: PropTypes.string,
        name: PropTypes.string,
    }),
    editMode: PropTypes.bool,
    embedlyUrl: PropTypes.string,
    previewDisabled: PropTypes.bool.isRequired,
    image: PropTypes.string,
    hash: PropTypes.string,
    handleCancelClick: PropTypes.func,
    handleUpdateClick: PropTypes.func,
    saveButtonDisabled: PropTypes.bool,
    handleTogglePostAnon: PropTypes.func,
    commentDepth: PropTypes.number,
};

ButtonPanel.defaultProps = {
    communityGroupHash: '',
    disablePhoto: false,
    pageGroup: {},
    handleCancelClick: {},
    handleUpdateClick: {},
    handleTogglePostAnon: {},
    embedlyUrl: '',
    editMode: false,
    image: null,
    saveButtonDisabled: false,
    hash: null,
    commentDepth: 0,
};

ButtonPanel.displayName = 'ButtonPanel';

export default ButtonPanel;
